/**
 * Rules (migration 0078) — user-made alarm/warning conditions, beside the locked system
 * catalogue. Mirrors `Backend/lumen-be/src/modules/rules/grammar.ts` byte-for-shape (not a
 * synced copy like the compliance engine — this is a request/response shape, not pure logic
 * that must produce identical output, so a plain hand-kept mirror is the right weight here).
 *
 * PERMISSIONS. Anyone signed in may list rules and see who they watch (`GET /rules` needs no
 * permission — same posture as the alarm board itself). Every write is `rules.manage`. Gate
 * the UI off `me.permissions`, never off `role` — the server enforces the same set per row.
 */
import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

export type RuleWhat =
  | { kind: 'metric'; key: string }
  | { kind: 'ratio'; numeratorKey: string; denominatorKey: string }
  | { kind: 'delta'; aKey: string; bKey: string }
  | { kind: 'sibling_ratio'; key: string; perKey?: string | undefined }
  /** A Lumen trend check (migration 0090): a number the server computes hourly from days of
   *  history. Only the platform's own system rules carry one; it cannot be authored or copied. */
  | { kind: 'trend'; check: TrendCheck; key: string };

export type TrendCheck =
  | 'heat_headroom'
  | 'heat_load_rise'
  | 'derate_hours'
  | 'weak_string'
  | 'string_days_to_half'
  | 'insulation_drop'
  | 'late_start'
  | 'conversion_drop'
  | 'sun_sensor_drift'
  | 'string_voltage_drop';

/** The comparison without the reading it is applied to. `at_least` / `at_most` include the
 *  bound itself (>= / <=); `not_equal` is `equals` negated, same optional tolerance. */
export type ConditionOp =
  | { op: 'above'; value: number }
  | { op: 'below'; value: number }
  | { op: 'at_least'; value: number }
  | { op: 'at_most'; value: number }
  | { op: 'equals'; value: number; tolerance?: number }
  | { op: 'not_equal'; value: number; tolerance?: number }
  | { op: 'between'; min: number; max: number }
  | { op: 'missing' }
  | { op: 'change_pct'; pct: number };

/** A comparison, optionally against its OWN reading. A leaf without `what` is judged against
 *  the rule's top-level `what` — the only shape a rule had before the drawer builder. */
export type ConditionLeaf = ConditionOp & { what?: RuleWhat };

export type ConditionNode = ConditionLeaf | { all: ConditionNode[] } | { any: ConditionNode[] };

export type RuleWhen = { mode: 'always' } | { mode: 'daylight' };

export type RuleWhere =
  | { kind: 'all' }
  | { kind: 'sites'; siteIds: string[] }
  | { kind: 'plants'; plantIds: string[] }
  | { kind: 'devices'; deviceIds: number[] }
  | { kind: 'role'; roleKey: string };

export type RuleOutcomeKind = 'warning' | 'alarm';
export type RuleSeverity = 1 | 3 | 6;

export type RuleClosing = { kind: 'auto'; afterMinutes: number } | { kind: 'manual' };

export interface RuleSpec {
  what: RuleWhat;
  condition: ConditionNode;
  forMinutes: number;
  when: RuleWhen;
  where: RuleWhere;
  outcome: RuleOutcomeKind;
  severity: RuleSeverity;
  closing: RuleClosing;
}

export interface RuleOffAtSite {
  siteId: string;
  siteName: string;
  reason: string;
  disabledBy: string;
  disabledByName: string | null;
  disabledAt: string;
}

/** Work order a rule raises for its own alarm (migration 0093) — mirrors the backend's
 *  `rules/automation.ts`. Bounds: due 1-720 h or none, wait 0-1440 min. */
export type RuleTicketPriority = 'low' | 'medium' | 'high' | 'urgent';

export interface RuleTicket {
  enabled: boolean;
  priority: RuleTicketPriority;
  /** Null raises it unassigned, for somebody to claim. */
  assigneeId: string | null;
  dueHours: number | null;
  /** Minutes the alarm must have been open first; 0 is immediately. */
  afterMinutes: number;
}

/** Mail a rule sends when its alarm opens (0093): up to ten addresses, and optionally the
 *  assignee. */
export interface RuleEmail {
  enabled: boolean;
  toAssignee: boolean;
  addresses: string[];
}

export interface RuleRow {
  id: string;
  kind: 'system' | 'org';
  name: string;
  category: string;
  /** What the rule is for, in its author's words (migration 0091); null when nobody said. */
  description: string | null;
  /** 1-10, how much a firing matters (0091). The alarm board ranks by it after severity. */
  weight: number;
  /** Null on every system rule and on a rule that never asked (0093). */
  ticket: RuleTicket | null;
  email: RuleEmail | null;
  builtin: string | null;
  spec: RuleSpec | null;
  alwaysOn: boolean;
  enabled: boolean;
  copiedFromRuleId: string | null;
  offAtSites: RuleOffAtSite[];
  /** Alarms OPENED under this rule in the trailing 7 days — a still-open one counts once. */
  firedThisWeek: number;
}

export interface RuleEngineHealth {
  lastRun: { startedAt: string; finishedAt: string | null; ok: boolean | null; error: string | null } | null;
  recentFailures: number;
}

export interface DryRunDeviceResult {
  deviceId: number;
  deviceName: string;
  fireCount: number;
  lastFiredAt: string | null;
}

export interface DryRunResult {
  wouldHaveFiredTotal: number;
  perDevice: DryRunDeviceResult[];
  bucketMinutes: number;
  devicesConsidered: number;
  daysSimulated: number;
}

const keys = {
  list: ['rules'] as const,
  health: ['rules', 'engine-health'] as const,
  readings: ['rules', 'readings'] as const,
};

/** One reading the organisation's devices actually send — the builder's picker rows. */
export interface ReadingOption {
  key: string;
  name: string;
  unit: string | null;
  devices: number;
  min: number | null;
  max: number | null;
  /** The newest of those readings, and its age by the server's clock. Optional for an older API. */
  latestAt?: string | null;
  ageSeconds?: number | null;
}

export interface ReadingGroup {
  roleKey: string | null;
  roleLabel: string;
  readings: ReadingOption[];
}

export function useRuleReadings(enabled = true) {
  return useQuery({
    queryKey: keys.readings,
    queryFn: () => api.get<{ groups: ReadingGroup[] }>('/rules/readings'),
    staleTime: 60_000,
    enabled,
  });
}

export function useRules(enabled = true) {
  return useQuery({
    queryKey: keys.list,
    queryFn: () => api.get<{ rules: RuleRow[] }>('/rules'),
    staleTime: 30_000,
    enabled,
  });
}

/**
 * A rule alarm's live identity is `rule:<uuid>` — this names it. Fetches the rule list only
 * once a rule alarm has actually arrived (`active`), so the shell does not pay for a request
 * nobody needs; the list is the same cached query the Rules view reads.
 */
export function useRuleNameOf(active: boolean): (id: string) => string | undefined {
  const q = useRules(active);
  const byId = useMemo(() => new Map((q.data?.rules ?? []).map((r) => [r.id, r.name])), [q.data]);
  return useCallback((id: string) => byId.get(id), [byId]);
}

export function useRuleEngineHealth() {
  return useQuery({
    queryKey: keys.health,
    queryFn: () => api.get<RuleEngineHealth>('/rules/engine-health'),
    refetchInterval: 60_000,
  });
}

function useRulesMutation<TVars>(fn: (vars: TVars) => Promise<{ rule: RuleRow }>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.list });
    },
  });
}

export interface CreateRuleInput {
  name: string;
  category: string;
  description?: string | undefined;
  weight?: number | undefined;
  ticket?: RuleTicket | undefined;
  email?: RuleEmail | undefined;
  spec: RuleSpec;
  copiedFromRuleId?: string | undefined;
}

export function useCreateRule() {
  return useRulesMutation((input: CreateRuleInput) => api.post<{ rule: RuleRow }>('/rules', input));
}

export interface UpdateRuleInput {
  id: string;
  name?: string | undefined;
  category?: string | undefined;
  /** An empty string clears it. */
  description?: string | undefined;
  weight?: number | undefined;
  ticket?: RuleTicket | undefined;
  email?: RuleEmail | undefined;
  spec?: RuleSpec | undefined;
  enabled?: boolean | undefined;
}

export function useUpdateRule() {
  return useRulesMutation(({ id, ...body }: UpdateRuleInput) =>
    api.patch<{ rule: RuleRow }>(`/rules/${id}`, body),
  );
}

export function useDeleteRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ ok: true }>(`/rules/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.list });
    },
  });
}

export function useDisableRuleAtSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, siteId, reason }: { id: string; siteId: string; reason: string }) =>
      api.post<{ ok: true }>(`/rules/${id}/disable-at-site`, { siteId, reason }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.list });
    },
  });
}

export function useEnableRuleAtSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, siteId }: { id: string; siteId: string }) =>
      api.post<{ ok: true }>(`/rules/${id}/enable-at-site`, { siteId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.list });
    },
  });
}

/** "Test before saving" — never persisted; see the backend's own dry-run header for the
 *  bucketed-approximation caveat this result's `bucketMinutes` is stating, not hiding. */
export function useTestRule() {
  return useMutation({
    mutationFn: (vars: { spec: RuleSpec; days?: number | undefined }) =>
      api.post<DryRunResult>('/rules/test', vars),
  });
}

export function useCloseRuleAlarm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (alarmId: number) => api.post<{ ok: true }>(`/rules/alarms/${alarmId}/close`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['alarms'] });
    },
  });
}
